
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { Download, FileText, Loader2, Trash2 } from "lucide-react";
import { useNoteAttachmentStore } from "@/stores/StoreProvider";
import { noteAttachmentsApi } from "@/lib/api/crm";
import type { NoteAttachment } from "@/types/crm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { DeleteConfirmation } from "@/components/ui/delete-confirmation";

// SAS URLs expire after 1h on the backend. Cache for 45min so we re-fetch well before expiry.
const SAS_STALE_MS = 45 * 60 * 1000;

const API_BASE = import.meta.env.VITE_API_URL ?? "";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Fetches the SAS URLs for one note on demand and caches them for 45min.
// `enabled` should be true only when the store says this note has attachments.
function useNoteAttachmentUrls(noteId: string, hasAttachments: boolean) {
  return useQuery({
    queryKey: ["attachment-urls", noteId],
    queryFn: () => noteAttachmentsApi.getByNote(noteId),
    enabled: hasAttachments,
    staleTime: SAS_STALE_MS,
    gcTime: SAS_STALE_MS + 60_000,
  });
}

// ── Single attachment row/thumbnail that resolves its own SAS URL JIT ────────

interface AttachmentItemProps {
  attachment: { id: string; noteId: string; fileName: string; contentType: string; sizeBytes: number };
  resolvedUrl: string | undefined;
  isFetchingUrls: boolean;
  onDelete?: (attachmentId: string) => void;
  isDeleting?: boolean;
  onImageClick?: (url: string, name: string) => void;
}

function AttachmentItem({ attachment, resolvedUrl, isFetchingUrls, onDelete, isDeleting, onImageClick }: AttachmentItemProps) {
  const isImage = attachment.contentType.startsWith("image/");
  const url = resolvedUrl ? (resolvedUrl.startsWith("/") ? `${API_BASE}${resolvedUrl}` : resolvedUrl) : undefined;

  if (isImage) {
    return (
      <div className="relative group">
        <button
          type="button"
          className="block rounded-md overflow-hidden border"
          onClick={() => url && onImageClick?.(url, attachment.fileName)}
          disabled={!url}
        >
          {url ? (
            <img src={url} alt={attachment.fileName} className="h-16 w-16 object-cover" />
          ) : (
            <div className="h-16 w-16 flex items-center justify-center bg-muted">
              {isFetchingUrls ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : <FileText className="h-4 w-4 text-muted-foreground" />}
            </div>
          )}
        </button>
        {onDelete && (
          <Button
            variant="destructive"
            size="icon"
            className="absolute -top-1.5 -right-1.5 h-5 w-5 rounded-full opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity"
            onClick={() => onDelete(attachment.id)}
            disabled={isDeleting}
            aria-label="Delete attachment"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs group">
      <FileText className="h-4 w-4 text-muted-foreground shrink-0" />
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[150px]">
          {attachment.fileName}
        </a>
      ) : (
        <span className="truncate max-w-[150px] text-muted-foreground">{attachment.fileName}</span>
      )}
      <Badge variant="secondary" className="text-xs px-1 py-0">
        {formatFileSize(attachment.sizeBytes)}
      </Badge>
      {isFetchingUrls && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      {onDelete && (
        <Button
          variant="ghost"
          size="icon"
          className="h-5 w-5 p-0 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100"
          onClick={() => onDelete(attachment.id)}
          disabled={isDeleting}
          aria-label="Delete attachment"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      )}
    </div>
  );
}

// ── Public component ─────────────────────────────────────────────────────────

interface NoteAttachmentGalleryProps {
  noteId: string;
  onDelete?: (attachmentId: string) => void;
  isDeleting?: boolean;
  className?: string;
  // Optional override — used in edit mode where the caller already has the attachment list
  // with SAS URLs from a fresh API response (e.g. just after upload).
  resolvedAttachments?: NoteAttachment[];
}

export const NoteAttachmentGallery = observer(function NoteAttachmentGallery({
  noteId,
  onDelete,
  isDeleting,
  className,
  resolvedAttachments,
}: NoteAttachmentGalleryProps) {
  const noteAttachmentStore = useNoteAttachmentStore();
  const localAttachments = noteAttachmentStore.getByNoteId(noteId);

  // If caller supplies fully-resolved attachments (edit mode after upload), use those.
  // Otherwise use local metadata from IDB + lazy SAS fetch.
  const useResolved = !!resolvedAttachments;
  // Only fire the API call when there are actually attachments in the store.
  const hasLocal = localAttachments.length > 0 || (resolvedAttachments?.length ?? 0) > 0;
  const { data: fetchedAttachments, isFetching } = useNoteAttachmentUrls(noteId, !useResolved && hasLocal);
  // Only fire the fetch query when we have metadata in the store but no resolved override
  const urlMap = useResolved
    ? new Map((resolvedAttachments ?? []).map(a => [a.id, a.url]))
    : new Map((fetchedAttachments ?? []).map(a => [a.id, a.url]));

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const [lightboxName, setLightboxName] = useState("");
  const [deleteAttId, setDeleteAttId] = useState<string | null>(null);

  const items = useResolved
    ? (resolvedAttachments ?? []).map(a => ({ id: a.id, noteId, fileName: a.fileName, contentType: a.contentType, sizeBytes: a.sizeBytes }))
    : localAttachments;

  if (items.length === 0) return null;

  const containerClass = className ?? "mt-1.5";
  const images = items.filter(a => a.contentType.startsWith("image/"));
  const files = items.filter(a => !a.contentType.startsWith("image/"));
  const deleteTarget = items.find(a => a.id === deleteAttId);

  return (
    <>
      {images.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${containerClass}`}>
          {images.map(att => (
            <AttachmentItem
              key={att.id}
              attachment={att}
              resolvedUrl={urlMap.get(att.id)}
              isFetchingUrls={isFetching}
              onDelete={onDelete ? () => setDeleteAttId(att.id) : undefined}
              isDeleting={isDeleting}
              onImageClick={(url, name) => { setLightboxUrl(url); setLightboxName(name); }}
            />
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className={`flex flex-wrap gap-2 ${images.length === 0 ? containerClass : "mt-1.5"}`}>
          {files.map(att => (
            <AttachmentItem
              key={att.id}
              attachment={att}
              resolvedUrl={urlMap.get(att.id)}
              isFetchingUrls={isFetching}
              onDelete={onDelete ? () => setDeleteAttId(att.id) : undefined}
              isDeleting={isDeleting}
            />
          ))}
        </div>
      )}

      <Dialog open={!!lightboxUrl} onOpenChange={open => !open && setLightboxUrl(null)}>
        <DialogContent className="max-w-3xl p-2">
          <DialogTitle className="sr-only">{lightboxName}</DialogTitle>
          {lightboxUrl && (
            <div className="flex flex-col items-center gap-2">
              <img src={lightboxUrl} alt={lightboxName} className="max-h-[80vh] w-auto rounded" />
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>{lightboxName}</span>
                <a href={lightboxUrl} target="_blank" rel="noopener noreferrer">
                  <Download className="h-4 w-4" />
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {onDelete && (
        <DeleteConfirmation
          open={!!deleteAttId}
          onOpenChange={open => !open && setDeleteAttId(null)}
          onConfirm={() => { if (deleteAttId) { onDelete(deleteAttId); setDeleteAttId(null); } }}
          title="Delete file?"
          entityType="attachment"
          entityName={deleteTarget?.fileName}
          isPending={isDeleting}
        />
      )}
    </>
  );
});
